import React, { useEffect, useState } from 'react';
import {
  Megaphone, Loader2, RefreshCw, Plus, Trash2, Pencil, CheckCircle,
  Calendar, Target, Users, X, Play, Pause, Archive,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminFetch } from '../lib/api';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: { label: 'Entwurf', color: 'var(--text-muted)' },
  active: { label: 'Aktiv', color: '#10B981' },
  paused: { label: 'Pausiert', color: '#F59E0B' },
  completed: { label: 'Abgeschlossen', color: '#3B82F6' },
  archived: { label: 'Archiviert', color: '#6B7280' },
};

const FUNNEL_STAGES = ['tofu', 'mofu', 'bofu'];
const FUNNEL_LABELS: Record<string, string> = { tofu: '🔝 Top of Funnel', mofu: '🔄 Middle of Funnel', bofu: '💰 Bottom of Funnel' };

export const MarketingCampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({
    name: '', audience: ['Fahrschüler 17-25'], funnelStage: 'tofu',
    coreMessage: '', targetKpi: '', startDate: '', endDate: '',
  });

  useEffect(() => { loadCampaigns(); }, []);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const resp = await adminFetch(`/api/admin/marketing/campaigns`, {
              });
      if (resp.ok) setCampaigns(await resp.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    const url = editing
      ? `/api/admin/marketing/campaigns/${editing.id}`
      : `/api/admin/marketing/campaigns`;
    await adminFetch(url, {
      method: editing ? 'PUT' : 'POST',
      body: JSON.stringify(form),
    });
    setShowModal(false);
    setEditing(null);
    setForm({ name: '', audience: ['Fahrschüler 17-25'], funnelStage: 'tofu', coreMessage: '', targetKpi: '', startDate: '', endDate: '' });
    await loadCampaigns();
  };

  const handleStatusChange = async (id: string, status: string) => {
    await adminFetch(`/api/admin/marketing/campaigns/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    await loadCampaigns();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Kampagne wirklich löschen?')) return;
    await adminFetch(`/api/admin/marketing/campaigns/${id}`, {
      method: 'DELETE',     });
    await loadCampaigns();
  };

  const openEdit = (c: any) => {
    setEditing(c);
    setForm({
      name: c.name, audience: c.audience || ['Fahrschüler 17-25'],
      funnelStage: c.funnel_stage || 'tofu', coreMessage: c.core_message || '',
      targetKpi: c.target_kpi || '', startDate: c.start_date?.split('T')[0] || '',
      endDate: c.end_date?.split('T')[0] || '',
    });
    setShowModal(true);
  };

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><Megaphone size={24} /> Kampagnen</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  const active = campaigns.filter(c => c.status === 'active').length;
  const draft = campaigns.filter(c => c.status === 'draft').length;

  return (
    <div>
      <div className="page-header">
        <h1><Megaphone size={24} /> Kampagnen</h1>
        <div className="flex gap-2">
          <button className="btn btn-primary" onClick={() => { setEditing(null); setForm({ name: '', audience: ['Fahrschüler 17-25'], funnelStage: 'tofu', coreMessage: '', targetKpi: '', startDate: '', endDate: '' }); setShowModal(true); }}>
            <Plus size={14} /> Neue Kampagne
          </button>
          <button className="btn btn-secondary" onClick={loadCampaigns}><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid-cols-4 mb-4">
        <div className="kpi-card"><span className="kpi-label">Kampagnen</span><span className="kpi-value">{campaigns.length}</span></div>
        <div className="kpi-card"><span className="kpi-label">Aktiv</span><span className="kpi-value" style={{ color: '#10B981' }}>{active}</span></div>
        <div className="kpi-card"><span className="kpi-label">Entwürfe</span><span className="kpi-value" style={{ color: 'var(--text-muted)' }}>{draft}</span></div>
        <div className="kpi-card"><span className="kpi-label">Abgeschlossen</span><span className="kpi-value" style={{ color: '#3B82F6' }}>{campaigns.filter(c => c.status === 'completed').length}</span></div>
      </div>

      {/* Campaigns Grid */}
      {campaigns.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
          <Megaphone size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>Keine Kampagnen. Erstelle deine erste Marketing-Kampagne.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {campaigns.map(c => {
            const statusConf = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft;
            return (
              <div key={c.id} className="card" style={{ padding: 18 }}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16 }}>{c.name}</h3>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 12, fontWeight: 600, background: `${statusConf.color}15`, color: statusConf.color }}>
                      {statusConf.label}
                    </span>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {FUNNEL_LABELS[c.funnel_stage] || c.funnel_stage}
                  </span>
                </div>

                {c.core_message && (
                  <p className="text-xs text-muted" style={{ lineHeight: 1.5, marginBottom: 8 }}>{c.core_message}</p>
                )}

                <div className="flex gap-2 items-center text-xs text-muted mb-3">
                  {c.start_date && (
                    <span className="flex items-center gap-1"><Calendar size={10} /> {new Date(c.start_date).toLocaleDateString('de-DE')}</span>
                  )}
                  {c.end_date && <span>→ {new Date(c.end_date).toLocaleDateString('de-DE')}</span>}
                  {c.target_kpi && <span className="flex items-center gap-1"><Target size={10} /> {c.target_kpi}</span>}
                </div>

                {c.audience?.length > 0 && (
                  <div className="flex gap-1 mb-3" style={{ flexWrap: 'wrap' }}>
                    {c.audience.map((a: string, i: number) => (
                      <span key={i} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(139,92,246,0.1)', color: '#8B5CF6' }}>
                        <Users size={8} style={{ verticalAlign: 'middle' }} /> {a}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex gap-1">
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)} title="Bearbeiten"><Pencil size={12} /></button>
                  {c.status === 'draft' && (
                    <button className="btn btn-sm" style={{ background: '#10B981', color: '#fff', border: 'none' }}
                      onClick={() => handleStatusChange(c.id, 'active')} title="Aktivieren"><Play size={12} /></button>
                  )}
                  {c.status === 'active' && (
                    <button className="btn btn-sm" style={{ background: '#F59E0B', color: '#fff', border: 'none' }}
                      onClick={() => handleStatusChange(c.id, 'paused')} title="Pausieren"><Pause size={12} /></button>
                  )}
                  {c.status === 'paused' && (
                    <button className="btn btn-sm" style={{ background: '#10B981', color: '#fff', border: 'none' }}
                      onClick={() => handleStatusChange(c.id, 'active')} title="Fortsetzen"><Play size={12} /></button>
                  )}
                  {['active', 'paused'].includes(c.status) && (
                    <button className="btn btn-sm" style={{ background: '#3B82F6', color: '#fff', border: 'none' }}
                      onClick={() => handleStatusChange(c.id, 'completed')} title="Abschließen"><CheckCircle size={12} /></button>
                  )}
                  <div style={{ flex: 1 }} />
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)} title="Löschen"><Trash2 size={12} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowModal(false)}>
          <div className="card" style={{ padding: 24, width: 500, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 style={{ margin: 0 }}>{editing ? 'Kampagne bearbeiten' : 'Neue Kampagne'}</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}><X size={14} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label className="text-xs text-muted mb-1" style={{ display: 'block' }}>Name *</label>
                <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="z.B. Frühlings-Kampagne 2026" style={{ width: '100%' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="text-xs text-muted mb-1" style={{ display: 'block' }}>Startdatum</label>
                  <input type="date" className="input" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} style={{ width: '100%' }} />
                </div>
                <div>
                  <label className="text-xs text-muted mb-1" style={{ display: 'block' }}>Enddatum</label>
                  <input type="date" className="input" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} style={{ width: '100%' }} />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted mb-1" style={{ display: 'block' }}>Funnel-Stufe</label>
                <select className="input" value={form.funnelStage} onChange={e => setForm(f => ({ ...f, funnelStage: e.target.value }))} style={{ width: '100%' }}>
                  {FUNNEL_STAGES.map(s => <option key={s} value={s}>{FUNNEL_LABELS[s]}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted mb-1" style={{ display: 'block' }}>Kernbotschaft</label>
                <textarea className="input" value={form.coreMessage} onChange={e => setForm(f => ({ ...f, coreMessage: e.target.value }))}
                  rows={2} placeholder="Was soll die Kampagne vermitteln?" style={{ width: '100%', resize: 'vertical', fontSize: 13 }} />
              </div>
              <div>
                <label className="text-xs text-muted mb-1" style={{ display: 'block' }}>Ziel-KPI</label>
                <input className="input" value={form.targetKpi} onChange={e => setForm(f => ({ ...f, targetKpi: e.target.value }))}
                  placeholder="z.B. 500 Downloads, 10% Engagement-Rate" style={{ width: '100%' }} />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button className="btn btn-primary" onClick={handleSave} disabled={!form.name.trim()}>
                <CheckCircle size={14} /> {editing ? 'Speichern' : 'Erstellen'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
